import { Button, Form, Input, Modal, Spin } from 'antd';
import dynamic from 'next/dynamic';
import { array, bool, func, object, string } from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import { DialogTrigger } from 'react-aria-components';
import { tv } from 'tailwind-variants';

import { focusRing } from 'components/ui/component-utils';
import { Button as ButtonLab } from 'components/ui/button';
import { Drawer as DrawerLab, DrawerRoot, DrawerX } from 'components/ui/drawer';
import { Select, SelectItem } from 'components/ui/select';
import { Button as LabButton } from 'components/ui/button';
import { LabCloseIcon } from 'components/camperlab/icons/close';
import { getProductsRecommended, notifyMe } from 'actions/products';
import FormInput from 'components/formInput';
import { CloseModalSVG } from 'components/iconsSVG/close';
import OldProtectionDataModal from 'components/protectionDataModal';
import { PrivacyPolicy } from 'components/privacyPolicy';
import { ThankYouMessage } from 'components/thankYouMessage';
import { useI18n } from 'context/i18n';
import { IS_CAMPER, IS_NNORMAL } from 'utils/constants/system';
import { getMarket } from 'utils/helpers';

import { useIsMounted, useValidate } from 'hooks';
import { isBlank } from 'utils/StringUtils';

import styles from './style.module.css';
import { getDomainValue } from 'utils/domain';
import { TextField } from 'components/ui/text-field';

const NnormalSVG = dynamic(() => import('components/iconsSVG/nnormal').then((mod) => mod.NnormalSVG));
const NotifySVG = dynamic(() => import('components/iconsSVG/notify').then((mod) => mod.NotifySVG));
const SizesList = dynamic(() => import('components/sizesList'));
const ProductsRows = dynamic(() => import('components/ProductsRow'));

export function NotifyMeModal({ code, isModalVisible, handleCancel, productSheet, defaultSize, camperlab = false }) {
  const { t, locale } = useI18n();
  const validate = useValidate();
  const isMounted = useIsMounted();
  const [loading, setLoading] = useState(false);
  const [notifyForm] = Form.useForm();
  const [finished, setFinished] = useState(false);
  const [selectedSize, setSelectedSize] = useState(defaultSize);
  const loadingChanged = useRef(false);
  const [recommended, setRecommended] = useState([]);
  const hasFetchedRecommended = useRef(false);

  const sizesAvailable = productSheet.sizes
    .filter((s) => !isBlank(s.value))
    .map((s) => ({
      ...s,
      available: productSheet.notifyMe && productSheet.notifyMeType !== 'SIZE' ? true : !s.available,
    }))
    .filter((s) => s.available);

  const onFinish = async (values) => {
    setLoading(true);
    try {
      if (values?.privacy === true) {
        await notifyMe({
          profile: locale,
          name: values.name,
          email: values.email,
          size: selectedSize,
          productId: code,
          newsletter: true,
          notifymeForSize: productSheet?.notifyMeType === 'SIZE',
          lab: getDomainValue({ camper: camperlab === true, camperlab: true, nnormal: false }),
        });
      }
      setLoading(false);
    } catch (error) {
      console.error(error);
      setLoading(false);
    }
  };

  useEffect(() => {
    if (loadingChanged.current === true && loading === false) {
      setFinished(true);
    } else {
      loadingChanged.current = true;
    }
  }, [loading]);

  useEffect(() => {
    setSelectedSize(defaultSize);
  }, [defaultSize]);

  useEffect(() => {
    if (!hasFetchedRecommended.current) {
      hasFetchedRecommended.current = true;
      const fetchRecommended = async () => {
        try {
          const response = await getProductsRecommended({
            lang: locale,
            productCode: productSheet.code,
            ia: 'true',
            target: IS_NNORMAL ? productSheet.target : undefined,
            forModal: true,
            targetIdUrl: productSheet.realTarget,
          });
          if (isMounted()) {
            setRecommended(() =>
              response.recommendeds.map((p) => {
                delete p.colors;
                delete p.subtitleH1;
                delete p.sizes;
                return p;
              }),
            );
          }
        } catch (e) {
          console.error('Fallo la llamada de productos recomendados', e);
        }
      };
      fetchRecommended();
    }
  }, [isModalVisible]);

  const notifyMeType = productSheet.notifyMeType !== null ? `.${productSheet.notifyMeType}` : '';
  const specialTxt = productSheet.notifyMeSpecialLabel ? '.special' : '';
  const market = getMarket(locale);

  return (
    <Modal
      title=""
      visible={isModalVisible}
      footer={null}
      onCancel={handleCancel}
      wrapClassName={`${styles.modalWrapper} ${finished === true ? styles.finished : ''} ${Array.isArray(recommended) && recommended.length > 0 ? styles.withRecommendations : ''}`}
      closeIcon={<CloseModalSVG />}
    >
      <div className={styles.modalContent}>
        {finished === false ?
          <>
            {IS_CAMPER ?
              <NotifySVG />
            : <NnormalSVG />}
            <p>{t('ficha.producto', 'modal.members.only.title', 'Members Only')}</p>
            <p className={styles.title}>{t('ficha.producto', 'notify.titulo', 'Not available product')}</p>
            <p className={styles.subtitle}>{t('ficha.producto', `notify.subtitulo${notifyMeType}${specialTxt}`, 'En estos momentos...')}</p>
            {sizesAvailable?.length > 0 && (
              <div className={styles.sizesDiv}>
                <p className={styles.sizesTitle}>{t('ficha.producto', 'notify.selectsize', 'Choose one size to check in-store availability.')}</p>
                <div className={styles.sizeListWrapper}>
                  <SizesList
                    sizes={sizesAvailable}
                    defaultSelectedSize={selectedSize}
                    onChange={setSelectedSize}
                    select={IS_NNORMAL}
                    productSheet={productSheet}
                    currentCountry={market}
                  />
                </div>
              </div>
            )}

            <p className={styles.subtitle}>{t('ficha.producto', 'notify.desc', 'Sin embargo, nuestro objetivo es garantizar...')}</p>

            <Form
              form={notifyForm}
              layout="vertical"
              name="notifyMe"
              onFinish={onFinish}
              requiredMark="optional"
              scrollToFirstError
              className={styles.customForm}
            >
              <FormInput name="name" rules={validate('name')} floatLabel="" required>
                <Input placeholder="Name" />
              </FormInput>
              <FormInput name="email" rules={validate('email')} required>
                <Input placeholder="Email" type="email" formNoValidate />
              </FormInput>

              <PrivacyPolicy hideConditions wrapperClass={styles.noBottomMargin} checkBoxClass={styles.customCheckbox} />

              <Spin spinning={loading}>
                <Form.Item>
                  <Button type="default" htmlType="submit" className={styles.submitBtn}>
                    {t('modal.idiomas', 'submit')}
                  </Button>
                </Form.Item>
              </Spin>
            </Form>
          </>
        : <>
            <ThankYouMessage type="notifyme" />
            {Array.isArray(recommended) && recommended.length > 0 ?
              <>
                <p className={styles.recommendedTitle}>{t('ficha.producto', 'tambien.puede.interesarte')}</p>
                <ProductsRows
                  locale={locale}
                  source={recommended}
                  currentList="You_may_also_be_interested"
                  slider={true}
                  camperlab={productSheet.camperlab}
                  withHover={false}
                  fixedGrid={false}
                  forcedSettings={{
                    dots: false,
                    infinite: false,
                    speed: 500,
                    slidesToShow: 4.33,
                    initialSlide: 0,
                    swipeToSlide: true,
                    easing: false,
                    draggable: true,
                    arrows: true,
                    swipe: true,
                    useTransform: false,
                    touchThreshold: 50,
                    responsive: [
                      {
                        breakpoint: 1440,
                        settings: {
                          arrows: false,
                          slidesToShow: 4.6,
                          slidesToScroll: 1,
                        },
                      },
                      {
                        breakpoint: 448,
                        settings: {
                          arrows: false,
                          slidesToShow: 2.2,
                          slidesToScroll: 1,
                        },
                      },
                    ],
                  }}
                />
              </>
            : null}
          </>
        }
      </div>
    </Modal>
  );
}

NotifyMeModal.propTypes = {
  isModalVisible: bool,
  handleCancel: func,
  code: string,
  productSheet: object,
  defaultSize: string,
};

function NotifyMeButton({ code, productSheet, defaultSize, camperlab }) {
  const { t } = useI18n();

  const [isModalVisible, setIsModalVisible] = useState(false);

  const showModal = () => {
    setIsModalVisible(true);
  };
  const handleCancel = () => {
    setIsModalVisible(false);
  };

  const notifyMeType = productSheet.notifyMeType !== null ? `.${productSheet.notifyMeType}` : '';

  return (
    <>
      {getDomainValue({
        camper: (
          <Button block size="large" type="primary" className={styles.notifyBtn} onClick={showModal}>
            {t('ficha.producto', `avisame${notifyMeType}`, 'NOTIFICARME')}
          </Button>
        ),
        nnormal: (
          <Button block size="large" type="primary" className={styles.notifyBtn} onClick={showModal}>
            {t('ficha.producto', `avisame${notifyMeType}`, 'NOTIFICARME')}
          </Button>
        ),
        camperlab: null,
      })}
      {getDomainValue({
        camper: (
          <NotifyMeModal
            code={code}
            handleCancel={handleCancel}
            isModalVisible={isModalVisible}
            productSheet={productSheet}
            defaultSize={defaultSize}
            camperlab={camperlab}
          />
        ),
        nnormal: (
          <NotifyMeModal
            code={code}
            handleCancel={handleCancel}
            isModalVisible={isModalVisible}
            productSheet={productSheet}
            defaultSize={defaultSize}
            camperlab={camperlab}
          />
        ),
        camperlab: null,
      })}
    </>
  );
}

NotifyMeButton.propTypes = {
  code: string,
  productSheet: object,
  defaultSize: string,
  recommendedProducts: array,
};

export default NotifyMeButton;
